import { Component } from '@theme/component';

/**
 * WAI-ARIA tabs pattern for the product Description/Reviews section.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tabs - The tab buttons.
 * @property {HTMLElement[]} panels - The tab panels.
 *
 * @extends Component<Refs>
 */
class ProductDetailsTabs extends Component {
  requiredRefs = ['tabs', 'panels'];

  /** @param {Event} event */
  activateTab(event) {
    const tab = event.currentTarget instanceof HTMLElement ? event.currentTarget : event.target;
    if (!(tab instanceof HTMLElement)) return;

    this.#select(tab);
    tab.focus();
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    const { tabs } = this.refs;
    const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (currentIndex === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;
    this.#select(nextTab);
    nextTab.focus();
  }

  /** @param {HTMLElement} tab */
  #select(tab) {
    const { tabs, panels } = this.refs;
    const targetId = tab.getAttribute('aria-controls');

    for (const t of tabs) {
      const isActive = t === tab;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', String(isActive));
      t.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of panels) {
      const isActive = panel.id === targetId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    }
  }
}

if (!customElements.get('product-details-tabs')) {
  customElements.define('product-details-tabs', ProductDetailsTabs);
}
